import { deepEqual } from 'fast-equals'
import { FC, memo, useContext, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import Swal from 'sweetalert2'

import { colsEquipment } from 'pages/costs/common/CostsColData'
import { buildEquipmentRowKey, normalizeToNumber } from 'pages/costs/common/equipmentSelectHelpers'
import { EquipmentAndConstructionTableBaseProps } from 'pages/costs/EquipmentAndConstruction/EquipmentAndConstructionPage'
import { clearDraft, getDraftSnapshot, setDraftFromRow } from 'pages/costs/EquipmentAndConstruction/equipmentRowStore'
import { UploadExcelModalBody } from 'pages/costs/modals/excel/UploadExcelModalBody'
import { NewEquipmentModalBody } from 'pages/costs/modals/NewEquipmentModalBody'

import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload'
import { ColData, colorConstants, MultipleDeleter, Table, UfinetButton, UfinetModal } from 'ufinet-web-components'
import {
	AuthContext,
	Authority,
	equipmentCostsService,
	ICostsEquipmentResponse,
	IUfinetSelectOption,
	onSelectionChangePreventDefaultsRemoval,
	useModal,
	zoneTypeMappings,
} from 'ufinet-web-functions'

type EquipmentTableProps = EquipmentAndConstructionTableBaseProps & {
	content: ICostsEquipmentResponse[]
	countryFilterOptions?: IUfinetSelectOption<string>[]
}

const EquipmentTable: FC<EquipmentTableProps> = memo(
	({
		className,
		content,
		findCosts,
		loadingRecords,
		pagingData,
		onPage,
		onSort,
		onFilter,
		onFilterClear,
		countryFilterOptions,
		clientNameFilter,
		areaNameFilter,
		updatedByFilter,
	}: EquipmentTableProps) => {
		const intl = useIntl()
		const [modal, showModal, hideModal] = useModal()
		const [newModal, showNewModal, hideNewModal] = useModal()
		const [selectedValues, setSelectedValues] = useState<ICostsEquipmentResponse[]>()
		const rowSnapshotsRef = useRef<Map<string, { snapshot: Record<string, any>; target: ICostsEquipmentResponse }>>(
			new Map()
		)
		const currentEditingRowKeyRef = useRef<string | null>(null)

		const authData = useContext(AuthContext)
		const roles = authData.userData?.authorities || []
		const permissions = Authority.getCostsPermissions(roles)

		const _equipmentCostsService = useMemo(() => equipmentCostsService(authData, intl), [authData, intl])

		// Ámbito uses a fixed catalog (Metropolitano / Larga distancia) so both options are always selectable,
		// regardless of which zone types are present in the currently loaded data.
		// Free-text Cliente/Área filters: see tableTextFilter.tsx for why (broken built-in select match mode).
		const cols: ColData[] = useMemo(() => {
			// Raw enum values as filter value, translated text only for display - the backend compares against
			// the stored enum value (see ZoneType), not the translated label.
			const zoneTypeOptions = [
				{ label: intl.formatMessage({ id: 'METROPOLITAN' }), value: 'METROPOLITAN' },
				{ label: intl.formatMessage({ id: 'LONG_DISTANCE' }), value: 'LONG_DISTANCE' },
			]
			return colsEquipment.map((column) => {
				if (column.field === 'countryName') return { ...column, filterOptions: countryFilterOptions }
				if (column.field === 'clientId') return clientNameFilter ? clientNameFilter.applyToColumn(column) : column
				if (column.field === 'networkAreaName') return areaNameFilter ? areaNameFilter.applyToColumn(column) : column
				if (column.field === 'lastModifiedBy') return updatedByFilter ? updatedByFilter.applyToColumn(column) : column
				if (column.field === 'zoneType') return { ...column, filterType: 'select', filterOptions: zoneTypeOptions }
				return column
			})
		}, [
			countryFilterOptions,
			intl,
			clientNameFilter?.applyToColumn,
			areaNameFilter?.applyToColumn,
			updatedByFilter?.applyToColumn,
		])

		const handleFilterClear = () => {
			clientNameFilter?.reset()
			areaNameFilter?.reset()
			updatedByFilter?.reset()
			onFilterClear?.()
		}

		const resolveNumericId = (...candidates: unknown[]): number | undefined => {
			for (const candidate of candidates) {
				const normalized = normalizeToNumber(candidate)
				if (normalized !== undefined) {
					return normalized
				}
			}

			return undefined
		}

		const getHeaderButtons = () => (
			<>
				{permissions.canDelete && (
					<MultipleDeleter
						setSelectedValues={setSelectedValues}
						selectedValues={selectedValues}
						deleteEndpoint={_equipmentCostsService.deleteByIdsList}
						search={findCosts}
					/>
				)}
				{permissions.canWrite && (
					<>
						<UfinetButton
							className="me-3"
							icon={faPlus}
							onClick={() => showNewModal()}
							content={intl.formatMessage({ id: 'NEW.REGISTER' })}
						/>
						<UfinetButton
							icon={faUpload}
							onClick={() => showModal()}
							content={intl.formatMessage({ id: 'BULK_LOAD' })}
						/>
					</>
				)}
			</>
		)

		const onRowEditComplete = (data: ICostsEquipmentResponse) => {
			const rowKey = buildEquipmentRowKey(data)
			rowSnapshotsRef.current.delete(rowKey)
			currentEditingRowKeyRef.current = null
			if (
				deepEqual(
					data,
					content.find((it) => it.id === data.id)
				)
			) {
				setDraftFromRow(rowKey, data, intl)
				return
			}

			const draft = getDraftSnapshot(rowKey)

			Swal.fire({
				icon: 'warning',
				title: intl.formatMessage({ id: 'COUNTRY.REGISTRY.UPDATE' }, { countryName: data.countryName }),
				html: intl.formatMessage({ id: 'COUNTRY.REGISTRY.UPDATE.CONFIRM' }, { networkAreaName: data.networkAreaName }),
				showCancelButton: true,
				showConfirmButton: true,
				reverseButtons: true,
				confirmButtonColor: colorConstants.primaryColor,
				cancelButtonColor: colorConstants.dangerColor,
				preConfirm: () => {
					const {
						id,
						countryId,
						countryName,
						networkAreaName,
						maximumCapacity,
						minimumCapacity,
						cost,
						laborCost,
						clientId,
						serviceTypeId,
						technicalAttributeId,
						transmissionMediumId,
					} = data

					const effectiveServiceTypeId = resolveNumericId(
						draft?.serviceTypeId,
						serviceTypeId,
						(data as any).serviceType
					)
					const effectiveTechnicalAttributeId = resolveNumericId(
						draft?.technicalAttributeId,
						technicalAttributeId,
						(data as any).technicalAttribute
					)
					const effectiveTransmissionMediumId = resolveNumericId(
						draft?.transmissionMediumId,
						transmissionMediumId,
						(data as any).transmissionMedium
					)

					if (
						effectiveServiceTypeId === undefined ||
						effectiveTechnicalAttributeId === undefined ||
						effectiveTransmissionMediumId === undefined
					) {
						Swal.showValidationMessage(
							intl.formatMessage({
								id: 'COST.EQUIPMENT.MISSING_SELECTIONS',
								defaultMessage: 'Complete los campos requeridos antes de guardar.',
							})
						)
						return false
					}

					_equipmentCostsService
						.update(id, {
							countryId,
							countryName,
							networkAreaName,
							zoneType: zoneTypeMappings.get(data.zoneType) || data.zoneType,
							maximumCapacity,
							minimumCapacity,
							cost,
							laborCost,
							clientId,
							serviceTypeId: effectiveServiceTypeId,
							technicalAttributeId: effectiveTechnicalAttributeId,
							transmissionMediumId: effectiveTransmissionMediumId,
						})
						.then(() => {
							clearDraft(rowKey)
							return findCosts()
						})
						.catch(console.error)
				},
			})
		}

		const onSelectionChange = (values: ICostsEquipmentResponse[]) => {
			onSelectionChangePreventDefaultsRemoval(
				values,
				selectedValues,
				content,
				setSelectedValues,
				intl.formatMessage({ id: 'BY_DEFAULT.DELETE.WARNING' })
			)
		}

		const handleRowEditInit = (event: any) => {
			const row = event?.data as ICostsEquipmentResponse | undefined
			if (!row) {
				return
			}
			const rowKey = buildEquipmentRowKey(row)
			rowSnapshotsRef.current.set(rowKey, { snapshot: { ...row }, target: row })
			setDraftFromRow(rowKey, row, intl)
			currentEditingRowKeyRef.current = rowKey
		}

		const handleRowEditCancel = () => {
			const rowKey = currentEditingRowKeyRef.current
			if (!rowKey) {
				return
			}
			const entry = rowSnapshotsRef.current.get(rowKey)
			const targetRow = entry?.target ?? content.find((it) => buildEquipmentRowKey(it) === rowKey)
			if (!targetRow) {
				rowSnapshotsRef.current.delete(rowKey)
				currentEditingRowKeyRef.current = null
				return
			}
			if (entry) {
				Object.assign(targetRow, entry.snapshot)
				rowSnapshotsRef.current.delete(rowKey)
			}
			setDraftFromRow(rowKey, targetRow, intl)
			currentEditingRowKeyRef.current = null
		}

		return (
			<>
				<Table
					selectedValues={selectedValues}
					onSelectionChange={permissions.canDelete ? onSelectionChange : undefined}
					onRowEditComplete={permissions.canUpdate ? onRowEditComplete : undefined}
					onRowEditInit={handleRowEditInit}
					onRowEditCancel={handleRowEditCancel}
					className={className}
					cols={cols}
					headerButtons={getHeaderButtons()}
					content={content}
					onFilterClear={handleFilterClear}
					emptyMessage={loadingRecords ? intl.formatMessage({ id: 'LOADING_DOTS' }) : undefined}
					lazySettings={
						pagingData && onPage && onSort && onFilter
							? {
									...pagingData,
									loading: loadingRecords,
									onPage,
									onSort,
									onFilter,
							  }
							: undefined
					}
				/>
				{/* Modals */}
				<UfinetModal
					size="xl"
					show={modal}
					handleClose={hideModal}
					title={intl.formatMessage({ id: 'MODAL.UPLOAD_EXCEL.TITLE' })}
				>
					<UploadExcelModalBody
						xlsEmptyDownloadCallback={_equipmentCostsService.downloadEmptyListXLS}
						xlsUploadCallback={_equipmentCostsService.uploadXLS}
						onXlsUploaded={findCosts}
						hideModal={hideModal}
					/>
				</UfinetModal>
				<UfinetModal
					size="lg"
					show={newModal}
					handleClose={hideNewModal}
					title={intl.formatMessage({ id: 'MODAL.NEW.REGISTER.TITLE' })}
				>
					<NewEquipmentModalBody findCosts={findCosts} hideModal={hideNewModal} />
				</UfinetModal>
			</>
		)
	},
	(prevProps, nextProps) => {
		return (
			deepEqual(prevProps.content, nextProps.content) &&
			deepEqual(prevProps.loadingRecords, nextProps.loadingRecords) &&
			deepEqual(prevProps.pagingData, nextProps.pagingData) &&
			deepEqual(prevProps.countryFilterOptions, nextProps.countryFilterOptions) &&
			prevProps.clientNameFilter?.applyToColumn === nextProps.clientNameFilter?.applyToColumn &&
			prevProps.areaNameFilter?.applyToColumn === nextProps.areaNameFilter?.applyToColumn &&
			prevProps.updatedByFilter?.applyToColumn === nextProps.updatedByFilter?.applyToColumn
		)
	}
)

export { EquipmentTable }
